import type { PagedResult } from '../../../components/index-page/types';
import { api } from '../../../lib/api';
import type { AuditLogDetail, AuditLogListItem } from './auditLogTypes';

export interface AuditLogQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  /** CSV — pass every entity type the module's workflow touches, parent and children. */
  entityType?: string;
  entityId?: string;
  userId?: string;
  /** CSV of module keys. */
  category?: string;
  action?: string;
  /** CSV of HTTP methods, e.g. `POST,DELETE`. */
  method?: string;
  from?: string;
  to?: string;
}

function toQueryString(query: AuditLogQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  /*
    Facets are sent whenever the key is present, empty string included — an empty value is the
    reader saying "none of these", which matches no row. A truthiness check here would drop it
    and the server would read the axis as unmentioned.
  */
  if (query.entityType !== undefined) params.set('entityType', query.entityType);
  if (query.entityId) params.set('entityId', query.entityId);
  if (query.userId) params.set('userId', query.userId);
  if (query.category !== undefined) params.set('category', query.category);
  if (query.action !== undefined) params.set('action', query.action);
  if (query.method !== undefined) params.set('method', query.method);
  if (query.from) params.set('from', query.from);
  if (query.to) params.set('to', query.to);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * The single audit endpoint pair — backend reference §8. It backs both the global page and
 * every per-module History Logs modal; there is deliberately no per-module history route.
 */
export const auditLogsApi = {
  list: (query: AuditLogQuery, signal?: AbortSignal) =>
    api<PagedResult<AuditLogListItem>>(`/api/audit-logs${toQueryString(query)}`, { signal }),

  get: (id: number, signal?: AbortSignal) =>
    api<AuditLogDetail>(`/api/audit-logs/${id}`, { signal }),
};
